import Controller, { OnAfterNavigate, OnBlur, OnChange, OnKeyUp, OnMount, OnSubmit, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FieldAPI, FormAPI, InputAPI, WizardAPI } from '@philips-internal/oswald-ui-components';
import { MountEvent, NavigateEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Value } from '@philips-internal/oswald-ui-core';
import { cast } from '../../utils/cast-helpers';
import BaseController from '../BaseController';
import AccountCreationBlackboard from '../account-creation/account-creation-blackboard';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import LoginTypeConstants from '../../constants/LoginTypeConstants';

export enum StepNames {
  PersonalIdentification = 'step-personal-identification',
  ReleaseAccess = 'step-release-access'
}

interface HealthInsurance {
  convenience: string;
}

@Controller({ fragment: 'new-user-by-login-steps', component: 'layout-root' })
export default class NewUserByLoginStepsController extends BaseController {

  @Inject('form-personal-identification')
  personalForm: FormAPI;

  @Inject('form-release-access')
  releaseForm: FormAPI;

  @Inject('AccountCreationBlackboard')
  accountCreationBlackboard: AccountCreationBlackboard;

  @Inject('wizard-account-creation')
  stepsAccountCreation: WizardAPI;

  @Inject('input-taxpayer')
  taxpayer: InputAPI;

  @Inject('type-of-access')
  typeAccess: FieldAPI;

  @Inject('field-user-login')
  userLogin: FieldAPI;

  @Inject('category')
  category: FieldAPI;

  @Inject('field-health-insurance')
  healthInsurance: FieldAPI;

  @Inject('field-email')
  emailField: FieldAPI;

  @Inject('field-prefix')
  prefix: FieldAPI;

  @Inject('input-email')
  inputEmail: InputAPI;

  @Inject('input-user-login')
  loginInput: InputAPI;

  allForms: FormAPI[] = [];
  type: string;
  emailAlreadyExists = false;
  userAlreadyExists = false;

  accessTypes = [
    { 'id': '2', 'loginAccess': MessageService.translate('results.insurance') },
    { 'id': '3', 'loginAccess': MessageService.translate('result.category') }
  ];

  @OnAfterNavigate({ target: 'wizard-account-creation' })
  onMainWizardAfterNavigate(event: NavigateEvent<string>): void {
    switch (event.to) {
      case StepNames.PersonalIdentification: {
        this.personalForm.setEditing(true);
        break;
      }
      case StepNames.ReleaseAccess: {
        const body = this.readAllFormsAndReturnBody();
        this.type = body.typeAccess as string;
        this.validateType(this.type);
        if (this.type === LoginTypeConstants.CD_HEALTH_INSURANCE) {
          this.category.removeValidator('required');
        } else {
          this.category.addValidator({ name: 'required' });
        }
        this.personalForm.setEditing(false);
        this.releaseForm.setEditing(true);
        break;
      }
    }
  }

  @OnMount({ target: ['autoc-type-of-access'] })
  onAutocTypeOfAccessMount(event: MountEvent): void {
    const onAutocTypeOfAccess = event.target as AutocompleteAPI;
    onAutocTypeOfAccess.setData(this.accessTypes );
  }

  @OnMount({ target: ['form-personal-identification', 'form-release-access'] })
  onMountAllForms(evt: MountEvent): void {
    this.allForms.push(cast(evt.target));
  }

  @OnSubmit({ target: ['wizard-account-creation'] })
  async onSubmitStepsAccountCreation(): Promise<void> {
    const body = this.readAllFormsAndReturnBody();
    this.accountCreationBlackboard.accountData = body;
    this.historyService.push(APPLICATION_ROUTES.NEW_USER_BY_LOGIN_PROCESSING);
  }

  @OnValidate({ target: ['form-personal-identification', 'form-release-access'] })
  onUserPersonalFormChangeStep(event: ValidateEvent): void {
    this.stepsAccountCreation.setStepDone(this.stepsAccountCreation.getCurrentStepIdentifier(), event.valid);
  }

  readAllFormsAndReturnBody(): Record<string, Value> {
    return this.allForms.map(form => form.getRecord())
      .map((record: Record<string, Value>) => {
        this.replaceValueWithNestedProperty(record, 'typeAccess', 'id');
        this.replaceValueWithNestedProperty(record, 'grade', 'cdCategory');
        return record;
      })
      .reduce((prev, curr) => Object.assign(prev, curr), {});
  }

  private replaceValueWithNestedProperty(object: Record<string, Value>, propertyName: string, nestedProperty: string): void {
    Object.keys(object)
      .filter(key => key === propertyName)
      .forEach(key => {
        const nestedObject: Record<string, Value> = cast(object[key]);
        object[key] = nestedObject[nestedProperty];
      });
  }

  @OnKeyUp({ target: 'input-taxpayer' })
  maskCpf(): void {
    let value = this.taxpayer.getValue().toString();
    value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    this.taxpayer.setValue(value);
  }

  @OnBlur({ target: 'input-email' })
  onBlurEmail(): void {
    const email = this.releaseForm.getRecord().accountEmail as string;

    HttpService.get(APPLICATION_ENDPOINTS.IS_EMAIL_VALID, { email })
      .then(() => {
        this.checkEmail();
      }).catch(async () => {
        if (!this.emailAlreadyExists) {
          this.emailAlreadyExists = true;
          this.emailField.addValidator({ name: 'emailAlreadyExistsValidator' });
          await this.emailField.validate();
        }
      });
  }

  @OnChange({ target: 'input-email' })
  onChangeEmail(): void {
    this.checkEmail();
  }

  @OnBlur({ target: 'input-user-login' })
  onBlurUser(): void {
    const prefix = this.releaseForm.getRecord().prefix as string;
    const user = this.releaseForm.getRecord().userLogin as string;
    const userLogin = prefix + user;

    HttpService.get(APPLICATION_ENDPOINTS.IS_USER_EXIST, { userLogin })
      .then(() => {
        this.checkUser();
      }).catch(async () => {
        if ( !this.userAlreadyExists ) {
          this.userAlreadyExists = true;
          this.userLogin.addValidator({ name: 'userAlreadyExistsValidator' });
          await this.userLogin.validate();
        }
      });
  }

  @OnChange({ target: 'input-user-login' })
  onChangeUser(): void {
    this.checkUser();
  }

  checkEmail(): void {
    if (this.emailAlreadyExists) {
      this.emailAlreadyExists = false;
      this.emailField.removeValidator('emailAlreadyExistsValidator');
    }
  }

  checkUser(): void {
    if ( this.userAlreadyExists ) {
      this.userAlreadyExists = false;
      this.userLogin.removeValidator('userAlreadyExistsValidator');
    }
  }

  validateType(type: string): void {
    switch (type) {
      case LoginTypeConstants.CD_HEALTH_INSURANCE: {
        this.category.setVisible(false);
        this.healthInsurance.setVisible(true);
        this.getConvenience();
        this.prefix.setValue('RP01-');
        break;
      }
      case LoginTypeConstants.CD_CATEGORY: {
        this.healthInsurance.setVisible(true);
        this.category.setVisible(true);
        this.getConvenience();
        this.prefix.setValue('RP02-');
        break;
      }
    }
  }

  @OnKeyUp({ target: 'input-user-login' })
  maskPostal(): void {
    let value = this.loginInput.getValue().toString();
    value = value.toUpperCase();
    this.loginInput.setValue(value);
  }

  getConvenience(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_CONVENIENCE).then((result) => {
      const convenienceDescription = result.body as HealthInsurance;
      this.healthInsurance.setValue(convenienceDescription.convenience);
    });
  }

}